import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import stampImage from "../../../assets/stamp.png";
import logoImage from "../../../assets/logoblack.png";
import { formatIndiaDateTime, formatIndiaDate, getToBeDeliveredFieldLabel } from "../../../utils/dateFormat";
import { formatProductDisplayName, getProductPlace } from "../orders/orderCalculations";

// Create StyleSheet for PDF
const styles = StyleSheet.create({
  page: {
    paddingTop: 35,
    paddingBottom: 48,
    paddingLeft: 40,
    paddingRight: 40,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  pageNumber: {
    position: "absolute",
    bottom: 18,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#333333",
  },
  headerSection: {
    marginBottom: 15,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: "#000000",
  },
  headerTop: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 0,
  },
  companyNameWithLogo: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 0,
  },
  companyLogo: {
    width: 35,
    height: 35,
    marginRight: 5,
  },
  companyTagline: {
    fontSize: 9,
    color: "#000",
    fontStyle: "italic",
    marginLeft: 40,
  },
  quotationInfo: {
    textAlign: "right",
  },
  quotationType: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#333",
  },
  revisionText: {
    fontSize: 8,
    color: "#000000",
    marginTop: 2,
  },
  quotationNumber: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#000000",
    marginTop: 3,
  },
  contactGrid: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 20,
  },
  contactColumn: {
    flex: 1,
  },
  contactLabel: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#000000",
    textTransform: "uppercase",
    marginBottom: 3,
  },
  contactText: {
    fontSize: 9,
    color: "#333",
    marginBottom: 2,
  },
  detailsGrid: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 15,
    marginBottom: 15,
  },
  detailsBox: {
    flex: 1,
    padding: 10,
    borderWidth: 1,
    borderRadius: 3,
  },
  customerBox: {
    borderColor: "#000000",
    backgroundColor: "#fff9f0",
  },
  datesBox: {
    borderColor: "#000000",
    backgroundColor: "#fff9f0",
  },
  detailsTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#000000",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  detailsGroup: {
    marginBottom: 3,
    fontSize: 9,
  },
  detailsLabel: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#333",
  },
  detailsValue: {
    fontSize: 9,
    color: "#333",
  },
  customerName: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 3,
  },
  tableWrapper: {
    marginBottom: 2,
  },
  tableHeader: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: "#ffffff",
    color: "#000000",
    borderTopWidth: 1,
    borderTopColor: "#000000",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
  },
  tableRow: {
    display: "flex",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    paddingVertical: 6,
  },
  tableHeaderCell: {
    fontSize: 9,
    fontWeight: "bold",
    paddingHorizontal: 4,
    paddingVertical: 6,
    textAlign: "center",
  },
  tableCell: {
    fontSize: 9,
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  colNum: {
    width: "5%",
    textAlign: "center",
    fontWeight: "bold",
    color: "#333",
  },
  colNumHeader: {
    width: "5%",
    textAlign: "center",
    fontWeight: "bold",
    color: "#000000",
  },
  colProduct: {
    width: "38%",
    textAlign: "left",
  },
  colQty: {
    width: "9%",
    textAlign: "center",
  },
  colArea: {
    width: "11%",
    textAlign: "center",
  },
  colUOM: {
    width: "8%",
    textAlign: "center",
  },
  colPrice: {
    width: "13%",
    textAlign: "right",
  },
  colAmount: {
    width: "16%",
    textAlign: "right",
  },
  productName: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 2,
  },
  productSpecs: {
    fontSize: 8,
    color: "#000",
    marginTop: 1,
  },
  amountText: {
    color: "#000000",
    fontWeight: "bold",
  },
  summarySection: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 25,
    marginTop: 10,
    marginBottom: 15,
  },
  termsBox: {
    flex: 1,
    padding: 8,
    borderWidth: 1,
    borderColor: "#000000",
    borderRadius: 3,
    backgroundColor: "#fff9f0",
  },
  summaryBox: {
    flex: 1,
    padding: 8,
    borderWidth: 1,
    borderColor: "#000000",
    borderRadius: 3,
    backgroundColor: "#fff9f0",
  },
  termsTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 4,
  },
  termsList: {
    fontSize: 8,
    color: "#333",
    lineHeight: 1.3,
  },
  summaryRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 3,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
  },
  summaryLabel: {
    fontSize: 9,
    color: "#333",
  },
  summaryValue: {
    fontSize: 9,
    color: "#333",
    fontWeight: "normal",
  },
  summaryValueNegative: {
    fontSize: 9,
    color: "#d32f2f",
    fontWeight: "normal",
  },
  grandTotalRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
    paddingTop: 3,
  },
  grandTotalLabel: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#000000",
  },
  grandTotalValue: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#000000",
  },
  footerSection: {
    borderTopWidth: 2,
    borderTopColor: "#000000",
    paddingTop: 15,
  },
  footerContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  footerLeft: {
    flex: 1,
    paddingRight: 12,
  },
  footerRight: {
    width: 130,
    alignItems: "center",
  },
  footerText: {
    fontSize: 9,
    color: "#000000",
    marginBottom: 3,
  },
  authorizedSignature: {
    fontSize: 9,
    color: "#000000",
    textAlign: "center",
    width: 130,
  },
  footerLabel: {
    fontWeight: "bold",
  },
  stampImage: {
    width: 80,
    height: 80,
    marginBottom: 6,
    alignSelf: "center",
  },
  signatureText: {
    fontSize: 8,
    color: "#000000",
  },
  dimText: {
    fontSize: 8,
    color: "#000",
  },
  dimNool: {
    fontSize: 6,
    position: "relative",
    top: -3,
  },
  finalNote: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 8,
    color: "#000000",
    fontStyle: "italic",
    borderTopWidth: 1,
    borderTopColor: "#000000",
    paddingTop: 8,
  },
});

export const QuotationTemplate = ({ order }: any) => {
  const totalAmount = parseFloat(order?.total) || 0;
  const transport = parseFloat(order?.transport) || 0;
  const fixing = parseFloat(order?.fixing) || 0;
  const discount = parseFloat(order?.discount) || 0;
  const gst = parseFloat(order?.gst) || 0;
  const grandTotal = totalAmount + transport + fixing + gst - discount;
  const advance = parseFloat(order?.advance) || 0;
  const advance2 = parseFloat(order?.advance2) || 0;
  const balance = Math.max(0, grandTotal - advance - advance2);

  const allProducts = order?.products || [];
  const gstRates = [...new Set(allProducts.map((p: any) => parseFloat(p.gst) || 0).filter((v: number) => v > 0))];
  const gstPercentLabel = gstRates.length === 1 ? ` (${gstRates[0]}%)` : gstRates.length > 1 ? ` (Mixed)` : "";

  const isInchUom = (uom: any) => {
    const u = String(uom || "").toLowerCase().trim();
    return u === "inch" || u === "in" || u === "inches";
  };

  const getNoolField = (p: any, kind: "width" | "height") => {
    const specs = p.specifications || {};
    const fromSpecs = (keys: string[]) => {
      for (const k of keys) {
        const v = specs[k];
        if (v === undefined || v === null || v === "") continue;
        const raw = typeof v === "object" && v !== null && "name" in v ? (v as any).name : v;
        if (raw !== undefined && raw !== null && raw !== "") return raw;
      }
      return undefined;
    };
    if (kind === "width") {
      return p.nool_width ?? p.noolWidth ?? fromSpecs(["nool_width", "noolWidth", "Nool Width"]) ?? 0;
    }
    return p.nool_height ?? p.noolHeight ?? fromSpecs(["nool_height", "noolHeight", "Nool Height"]) ?? 0;
  };

  const isNumericDimNoolSpec = (key: string, value: any) => {
    const k = String(key || "").toLowerCase().replace(/\s+/g, "_");
    if (k !== "nool_width" && k !== "nool_height" && k !== "noolwidth" && k !== "noolheight") return false;
    const raw = typeof value === "object" && value !== null && "name" in value ? (value as any).name : value;
    const n = parseFloat(raw);
    return raw !== "" && raw != null && !isNaN(n);
  };

  const formatSpecValue = (value: any) =>
    typeof value === "object" && value?.name ? value.name : value;

  const getDisplaySpecParts = (p: any): string[] => {
    const parts: string[] = [];
    const specs = p.specifications || {};
    const placeFromSpecs = Object.entries(specs).some(([key]) => String(key).toLowerCase() === "place");
    if (p.place && !placeFromSpecs) parts.push(`Place: ${p.place}`);
    Object.entries(specs).forEach(([key, value]: [string, any]) => {
      if (isNumericDimNoolSpec(key, value)) return;
      const display = formatSpecValue(value);
      if (display === undefined || display === null || display === "") return;
      parts.push(`${key}: ${display}`);
    });
    return parts;
  };

  const renderDimWithNool = (label: string, value: any, nool: any, uom: any) => {
    const noolNum = parseFloat(nool);
    const showNool = isInchUom(uom) && !isNaN(noolNum) && noolNum > 0;
    const uomDisplay = uom || "";
    return (
      <>
        <Text style={styles.dimText}>{label}: </Text>
        <Text style={styles.dimText}>{String(value ?? "")}</Text>
        {showNool ? <Text style={styles.dimNool}>{String(noolNum)}</Text> : null}
        {uomDisplay ? <Text style={styles.dimText}> {uomDisplay}</Text> : null}
      </>
    );
  };

  // Helper function to get field value from camelCase or snake_case
  const getFieldValue = (camelCase: string, snakeCase: string) => {
    return order?.[camelCase] || order?.[snakeCase];
  };

  const formatDate = formatIndiaDate;
  const formatIndiaTime = formatIndiaDateTime;
  const formatDateOnly = formatIndiaDate;

  // Header Component - Repeats on every page
  const Header = () => (
    <View style={styles.headerSection}>
      {/* Top Header */}
      <View style={styles.headerTop}>
        <View style={styles.companyInfo}>
          <View style={styles.companyNameWithLogo}>
            <Image src={logoImage} style={styles.companyLogo} />
            <Text style={styles.companyName}>Thaai Interiors</Text>
          </View>
          <Text style={styles.companyTagline}>"Best Choice For Awesome Feel"</Text>
        </View>
        <View style={styles.quotationInfo}>
          <Text style={styles.quotationType}>INVOICE</Text>
          {getFieldValue("invoiceId", "invoice_id_") && (
            <Text style={styles.quotationNumber}>#{getFieldValue("invoiceId", "invoice_id_")}</Text>
          )}
        </View>
      </View>

      {/* Contact Information */}
      <View style={styles.contactGrid}>
        <View style={styles.contactColumn}>
          <Text style={styles.contactLabel}>Business Address</Text>
          <Text style={styles.contactText}>258/1A, 200 Feet Service Road</Text>
          <Text style={styles.contactText}>Mettukuppam, Vanagaram</Text>
          <Text style={styles.contactText}>Chennai - 600095</Text>
          <Text style={{ ...styles.contactText, fontSize: 7 }}>(Near Porur Toll Plaza)</Text>
        </View>
        <View style={styles.contactColumn}>
          <Text style={styles.contactLabel}>Contact</Text>
          <Text style={styles.contactText}>Email: thaaiinteriors2019@gmail.com</Text>
          <Text style={styles.contactText}>Website: thaaiinteriors.com</Text>
          <Text style={{ ...styles.contactText, marginTop: 5 }}>GSTIN: 33AXWPG7852P1ZI</Text>
        </View>
        <View style={styles.contactColumn}>
          <Text style={styles.contactLabel}>Invoice Date</Text>
          <Text style={{ ...styles.contactText, fontSize: 12, color: "#000000", fontWeight: "bold" }}>
            {formatDate(order.invoiceDate)}
          </Text>
        </View>
      </View>
    </View>
  );

  // Details Section
  const DetailsSection = () => (
    <View style={styles.detailsGrid}>
      {/* Customer Details Box */}
      <View style={{ ...styles.detailsBox, ...styles.customerBox }}>
        <Text style={styles.detailsTitle}>Customer Details</Text>
        <Text style={styles.customerName}>{order.customerName}</Text>
        
        {order.company_name_ && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Company: </Text>
            {order.company_name_}
          </Text>
        )}
        
        {order.address_ && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Address: </Text>
            {order.address_}
          </Text>
        )}
        
        {(order.gst_ || order.customerGst_) && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>GST: </Text>
            {order.gst_ || order.customerGst_}
          </Text>
        )}
        
        {order.mobile && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Mobile: </Text>
            {order.mobile}
          </Text>
        )}
        
        {(order.website_ || order.Website_) && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Website: </Text>
            {order.website_ || order.Website_}
          </Text>
        )}
      </View>

      {/* Important Dates Box */}
      <View style={{ ...styles.detailsBox, ...styles.datesBox }}>
        <Text style={styles.detailsTitle}>Important Dates</Text>
        
        {getFieldValue("createdAt", "created_at") && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Created On: </Text>
            {formatIndiaTime(getFieldValue("createdAt", "created_at"))}
          </Text>
        )}
        
        {getFieldValue("updatedAt", "updated_at") && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Updated Date: </Text>
            {formatIndiaTime(getFieldValue("updatedAt", "updated_at"))}
          </Text>
        )}
        
        {getFieldValue("deliveryDate", "delivery_date") && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Delivery Date: </Text>
            {formatDateOnly(getFieldValue("deliveryDate", "delivery_date"))}
          </Text>
        )}
        
        {getFieldValue("toBeDelivered", "to_be_delivered") && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>{getToBeDeliveredFieldLabel(getFieldValue("customerRole", "customer_role_"))}: </Text>
            {formatDateOnly(getFieldValue("toBeDelivered", "to_be_delivered"))}
          </Text>
        )}
      </View>
    </View>
  );

  // Products Table Header
  const TableHeader = () => (
    <View style={styles.tableHeader}>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colNumHeader }}>#</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colProduct }}>Product Name</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colQty }}>Qty</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colArea }}>Area</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colUOM }}>UOM</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colPrice }}>Price/Unit</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colAmount }}>Amount</Text>
    </View>
  );

  // Summary and Footer Section
  const SummarySection = () => (
    <View style={styles.summarySection}>
      {/* Terms & Conditions */}
      <View style={styles.termsBox}>
        {order.terms && (
          <View style={{ marginBottom: order.notes ? 10 : 0 }}>
            <Text style={styles.termsTitle}>Terms & Conditions:</Text>
            {order.terms
              .split("\n")
              .filter((line: string) => line.trim())
              .map((line: string, index: number) => {
                // Clean up the line: remove tick symbols and extra whitespace
                const cleanedLine = line.trim().replace(/^[✓•–\-\*]\s*/, "").trim();
                return cleanedLine ? (
                  <Text key={index} style={styles.termsList}>
                    • {cleanedLine}
                  </Text>
                ) : null;
              })}
          </View>
        )}

        {order.notes && (
          <View>
            <Text style={styles.termsTitle}>Special Notes:</Text>
            <Text style={styles.termsList}>{order.notes}</Text>
          </View>
        )}
      </View>

      {/* Price Summary */}
      <View style={styles.summaryBox}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Subtotal:</Text>
          <Text style={styles.summaryValue}>Rs. {totalAmount.toFixed(2)}</Text>
        </View>

        {discount > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Discount:</Text>
            <Text style={styles.summaryValueNegative}>-Rs. {discount.toFixed(2)}</Text>
          </View>
        )}

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Fixing:</Text>
          <Text style={styles.summaryValue}>Rs. {fixing.toFixed(2)}</Text>
        </View>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Transport:</Text>
          <Text style={styles.summaryValue}>Rs. {transport.toFixed(2)}</Text>
        </View>

        {gst > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>GST{gstPercentLabel}:</Text>
            <Text style={styles.summaryValue}>Rs. {gst.toFixed(2)}</Text>
          </View>
        )}

        <View style={styles.grandTotalRow}>
          <Text style={styles.grandTotalLabel}>Grand Total:</Text>
          <Text style={styles.grandTotalValue}>Rs. {grandTotal.toFixed(2)}</Text>
        </View>

        {advance > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Advance 1:</Text>
            <Text style={styles.summaryValue}>Rs. {advance.toFixed(2)}</Text>
          </View>
        )}
        {advance2 > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Advance 2:</Text>
            <Text style={styles.summaryValue}>Rs. {advance2.toFixed(2)}</Text>
          </View>
        )}

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Balance Amount:</Text>
          <Text style={styles.summaryValue}>Rs. {balance.toFixed(2)}</Text>
        </View>
      </View>
    </View>
  );

  // Footer Section
  const Footer = () => (
    <View style={styles.footerSection}>
      {order.totalArea && (
        <Text style={styles.footerText}>
          <Text style={styles.footerLabel}>Total Area:</Text> {order.totalArea}
        </Text>
      )}
      
      <View style={styles.footerContent}>
        <View style={styles.footerLeft}>
          <Text style={styles.footerText}>
            <Text style={styles.footerLabel}>Date:</Text> {formatDate(order.createdAt)}
          </Text>
        </View>
        <View style={styles.footerRight}>
          <Image src={stampImage} style={styles.stampImage} />
          <Text style={styles.authorizedSignature} wrap={false}>
            Authorized Signature
          </Text>
        </View>
      </View>
    </View>
  );

  // Continuous flow — no artificial 10-product page chunks (avoids large empty gaps)

  // Component to render product rows for a specific page
  const renderPageProducts = (pageProducts: any, startIndex: number) => (
    <>
      {pageProducts.map((p: any, index: number) => {
        const actualIndex = startIndex + index;
        const specParts = getDisplaySpecParts(p);
        const isWidthHeight = !p.formulaType || p.formulaType === "width_height";
        const isLength = p.formulaType === "length";
        return (
          <View key={actualIndex} style={styles.tableRow} wrap={false}>
            <Text style={{ ...styles.tableCell, ...styles.colNum }}>
              {actualIndex + 1}
            </Text>
            <View style={{ ...styles.tableCell, ...styles.colProduct, width: "38%" }}>
              <Text style={styles.productName}>
                {formatProductDisplayName(p.name || p.description || "N/A", getProductPlace(p))}
              </Text>
              {specParts.length > 0 && (
                <Text style={styles.productSpecs}>{specParts.join(", ")}</Text>
              )}
              {isWidthHeight && (
                <View style={{ flexDirection: "row", alignItems: "flex-start", flexWrap: "wrap", marginTop: 1, paddingTop: 2 }}>
                  {renderDimWithNool("W", p.width, getNoolField(p, "width"), p.wh_uom)}
                  <Text style={styles.dimText}>  x  </Text>
                  {renderDimWithNool("H", p.height, getNoolField(p, "height"), p.wh_uom)}
                </View>
              )}
              {isLength && (
                <Text style={styles.productSpecs}>L: {p.length} {p.length_uom}</Text>
              )}
            </View>
            <Text style={{ ...styles.tableCell, ...styles.colQty }}>
              {p.nos || "-"}
            </Text>
            <Text style={{ ...styles.tableCell, ...styles.colArea }}>
              {p.formulaType === "pieces"
                ? "-"
                : parseFloat(p.area || "0").toFixed(2)}
            </Text>
            <Text style={{ ...styles.tableCell, ...styles.colUOM }}>
              {p.formulaType === "pieces" ? "Pcs." : p.area_uom || "-"}
            </Text>
            <Text style={{ ...styles.tableCell, ...styles.colPrice }}>
              Rs. {(parseFloat(p.price.toString()) / parseFloat(p.area || "1")).toFixed(2)}
            </Text>
            <Text style={{ ...styles.tableCell, ...styles.colAmount, ...styles.amountText }}>
              Rs. {parseFloat(p.price.toString()).toFixed(2)}
            </Text>
          </View>
        );
      })}
    </>
  );

  return (
    <Document title={`invoice-${getFieldValue("invoiceId", "invoice_id_") || order.id}`}>
      <Page size="A4" style={styles.page} wrap>
        <Header />
        <DetailsSection />
        <View style={styles.tableWrapper}>
          <TableHeader />
          {renderPageProducts(allProducts, 0)}
        </View>
        <View wrap={false}>
          <SummarySection />
          <Footer />
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber }) => `${pageNumber}`}
          fixed
        />
      </Page>
    </Document>
  );
};
